import { useEffect, useRef, useState } from 'react'
import profileIcon from '../../assets/icons/profile-icon.svg'
import Dropdown from '../Dropdown'
import FormInput from '../Input'
import { useFormik } from 'formik'
import DefaultButton from '../Button/Button'
import circle from '../../assets/icons/Circle.png'
import { editProfile } from '../../validations/editProfile'
import StaffService from '../../api/staff-service'
import { getLocal, setLocal } from '../../utils/local'
import { formatDate } from '../../utils/formatDate'
import Modal from '../Modal'
import { showToast } from '../../utils/toast'
import { useNavigate } from 'react-router-dom'

const EditProfileForm = () => {
  const [photoLoading, setPhotoLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: '',
      phone: '',
      address: '',
      dateOfBirth: '',
      gender: '',
      imageUrl: '',
      email: '',
      nationality: '',
    },
    validationSchema: editProfile,
    onSubmit: async (values) => {
      if (saveLoading) return

      const payload = {
        name: values.name,
        phone: values.phone,
        address: values.address,
        dateOfBirth: values.dateOfBirth,
        gender: values.gender as 'MALE' | 'FEMALE' | 'OTHERS',
        imageUrl: values.imageUrl
      }
      setSaveLoading(true);
      try {
        await StaffService.updateUserProfile(payload)
        await StaffService.getLoggedInStaff()

        showToast({
          title: 'Profile Updated',
          description: 'Your profile has been successfully updated.',
          id: 'update-success',
          status: 'success',
        });
        navigate('/settings');

      } catch (error) {
        console.error('Update failed', error)
      } finally {
        setSaveLoading(false)
      }
    },
  })

  useEffect(() => {
    const userProfile = getLocal('user')
    
    formik.setValues({
      name: userProfile.name,
      phone: userProfile.phone,
      address: userProfile.address,
      gender: userProfile.gender,
      nationality: userProfile.nationality,
      email: userProfile.email,
      imageUrl: userProfile.imageUrl,
      dateOfBirth: formatDate(userProfile.dateOfBirth),
    })
    setImageUrl(userProfile.imageUrl);
  }, [])

  const genderOptions = [
    { value: 'MALE', label: 'Male' },
    { value: 'FEMALE', label: 'Female' },
    { value: 'OTHERS', label: 'Others' },
  ]

  const nationalityOptions = [
    { value: 'nigerian', label: 'Nigerian' },
    { value: 'other', label: 'Other' },
  ]

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        showToast({
          title: 'Unsupported File Format',
          description: 'Please upload a valid image file, such as JPEG, PNG, GIF, or WebP.',
          id: 'invalid-file-type',
          status: 'error',
        });
        return;
      }
      setPhotoLoading(true); 
      try {
        const res = await StaffService.UploadStaffPhoto(file); 
        showToast({
          title: 'Upload Successful',
          description: 'Your profile photo has been updated.',
          id: 'upload-success',
          status: 'success',
        });
  
        const uploadedImageUrl = res.data.url; 
        setImageUrl(uploadedImageUrl); 
        formik.setFieldValue('imageUrl', uploadedImageUrl); 
        const userProfile = getLocal('user');
        const updatedUserProfile = { ...userProfile, imageUrl: uploadedImageUrl };
        setLocal('user', updatedUserProfile);
        
      } catch (error) {
        showToast({
          title: 'Upload Failed',
          description: 'There was an error uploading your photo. Please try again.',
          id: 'upload-error',
          status: 'error',
        });
      } finally {
        setPhotoLoading(false);
      }
    }
  };


  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="flex flex-col lg:flex-row lg:gap-10 gap-12 mb-6">
        <div className="flex flex-col gap-3 sm:items-center lg:items-start">
          <label className="text-[#6C757D] text-center">Profile image</label>
          <div className="bg-[#E9ECEF] h-[170px] relative w-[160px] rounded-lg flex flex-col items-center justify-center self-center">
            <img src={circle} alt="" className="absolute self-end top-0 right-0" onClick={handleUploadClick} />
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept='image/*'
              />
             <img src={imageUrl ? imageUrl : profileIcon} alt="Default Profile" className="h-full w-full object-cover rounded-lg" />
          </div>

        </div>
        {photoLoading && (
      <Modal
    open={photoLoading}
    close={() => setPhotoLoading(false)}
    modalClassName="flex flex-col items-center justify-center w-[300px] h-[200px]"
  >
    <p className="text-lg font-bold mb-4">Uploading Your Photo...</p>
    <div className="h-10 w-10 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
  </Modal>
)}

        <div className="flex flex-col flex-1 gap-5 md:gap-10">
          <div className="flex gap-5 md:gap-10 flex-grow flex-col lg:flex-row">
            <FormInput
              placeholder="Fullname"
              type="text"
              label="Fullname"
              name="name"
              size="lg"
              labelColor="#6C757D"
              borderRadius="none"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <FormInput
              placeholder="dolores.chambers@safelto.com"
              type="text"
              label="Email address"
              name="email"
              size="lg"
              labelColor="#6C757D"
              readonly
              borderRadius="none"
              value={formik.values.email}
              onChange={formik.handleChange}
            />
          </div>
          <div className="flex gap-5 md:gap-10 flex-col lg:flex-row">
            <FormInput
              placeholder="(505) 555-0125"
              type="text"
              label="Phone number"
              name="phone"
              size="lg"
              labelColor="#6C757D"
              borderRadius="none"
              value={formik.values.phone}
              onChange={formik.handleChange}
            />
            <FormInput
              placeholder="4517 Washington Ave. Manchester, Kentu"
              type="text"
              label="Address"
              name="address"
              size="lg"
              labelColor="#6C757D"
              borderRadius="none"
              value={formik.values.address}
              onChange={formik.handleChange}
            />
          </div>
        </div>
      </div>
      <div className="flex gap-5 md:gap-8 mb-14 md:mb-20 flex-col lg:flex-row">
        <div className="space-y-2.5 lg:w-5/12">
          <FormInput
            type="date"
            name="dateOfBirth"
            size="lg"
            label="Date of birth"
            labelColor="#6C757D"
            value={formik.values.dateOfBirth}
            onChange={formik.handleChange}
          />
        </div>
        <div className="space-y-2.5 lg:w-2/12">
          <p className="text-[#6C757D] font-medium text-base leading-7">Gender</p>
          <Dropdown
            options={genderOptions}
            onSelect={(value) => formik.setFieldValue('gender', value)}
            defaultLabel="Select gender"
            defaultValue={formik.values.gender}
          />
          {formik.touched.gender && formik.errors.gender && (
            <div className="text-red-500 text-sm">{formik.errors.gender}</div>
          )}
        </div>
        <div className="space-y-2.5 lg:w-5/12">
          <p className="text-[#6C757D] font-medium text-base leading-7">Nationality</p>
          <Dropdown
            options={nationalityOptions}
            onSelect={(value) => formik.setFieldValue('nationality', value)}
            defaultLabel="Select nationality"
            defaultValue={formik.values.nationality}
          />
          {formik.touched.nationality && formik.errors.nationality && (
            <div className="text-red-500 text-sm">{formik.errors.nationality}</div>
          )}
        </div>
      </div>
      <div className="flex flex-col-reverse sm:flex-row gap-4 justify-center sm:justify-end">
        <DefaultButton
          buttonType="secondary"
          border="none"
          fontSize="16"
          className="sm:w-[200px]"
          onClick={() => navigate('/settings')}
        >
          Cancel
        </DefaultButton>
        <DefaultButton
          type="submit"
          size="lg"
          fontSize="16"
          className="sm:w-[200px]"
          loading={saveLoading}
          loadingText="Saving..."
          disabled={saveLoading}
        >
          Save
        </DefaultButton>
      </div>
    </form>
  )
}

export default EditProfileForm
